import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import HelpDot from '../academy/HelpDot';
import StatDoughnut from './common/StatDoughnut';
import TopInstalledApps from './TopInstalledApps';
import { exportRowsToExcel } from '../utils/exportExcel';

const STATUS_PILL = {
  done: 'sf2-pill sf2-pill--ok',
  error: 'sf2-pill sf2-pill--bad',
  running: 'sf2-pill sf2-pill--info',
  queued: 'sf2-pill sf2-pill--info',
  pending: 'sf2-pill sf2-pill--info',
  cancelled: 'sf2-pill sf2-pill--wa',
};

const STATUS_LABEL = {
  done: 'Instalado',
  error: 'Error',
  running: 'Instalando',
  queued: 'En cola',
  pending: 'En cola',
  cancelled: 'Cancelado',
};

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('es-ES');
};

// ── Paquetes que instalan «por usuario» ───────────────────────────────────
// El agente corre como SYSTEM: si el instalador del paquete es per-user (no
// escribe en Program Files), el programa acaba en el perfil de la cuenta de
// servicio y ningún usuario lo ve en su menú Inicio. Chocolatey dirá que está
// instalado —y lo está— pero para SYSTEM.
//
// Mejor decirlo ANTES de desplegar que dejar al operador pensando que el
// despliegue falló. La lista es corta a propósito: solo los casos conocidos.
const PAQUETES_POR_USUARIO = {
  vivaldi: 'Vivaldi',
  'vivaldi.portable': 'Vivaldi (portable)',
  googlechrome: 'Google Chrome',
  'microsoft-edge': 'Microsoft Edge',
  brave: 'Brave',
  slack: 'Slack',
  zoom: 'Zoom',
};

function confirmarAmbito(packageId) {
  const nombre = PAQUETES_POR_USUARIO[String(packageId || '').toLowerCase()];
  if (!nombre) return true;
  return window.confirm(
    `${nombre} instala «por usuario», no para toda la máquina.\n\n`
    + 'El agente corre como SYSTEM, así que el programa quedará en el perfil de la cuenta de '
    + 'servicio y NO aparecerá en el menú Inicio de los usuarios.\n\n'
    + '¿Desplegarlo igualmente? Para el parque hace falta un paquete propio que instale a nivel '
    + 'de máquina.',
  );
}

const KPI = ({ label, value, tone }) => (
  <div
    style={{
      flex: '1 1 130px',
      minWidth: 130,
      border: '1px solid var(--border-color, #e5e7eb)',
      borderRadius: 10,
      padding: '10px 12px',
      background: 'var(--bg-card, #fff)',
    }}
  >
    <div style={{ fontSize: 22, fontWeight: 700, color: tone || 'inherit', lineHeight: 1.1 }}>
      {value}
    </div>
    <div className="sf2-hint" style={{ marginTop: 2 }}>{label}</div>
  </div>
);

/**
 * Consola de distribución de software con Chocolatey.
 *
 * Sin `ciId` es la consola central: KPIs del parque, selector de equipos,
 * catálogo predefinido, catálogo de la empresa y control de todo lo desplegado.
 * Con `ciId` es la tarjeta de la ficha: solo ese equipo.
 */
export default function SoftwarePackagesPanel({ companyId, ciType, ciId, hostName, compact = false }) {
  const perHost = Boolean(ciId);

  const [packages, setPackages] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [meta, setMeta] = useState({});
  const [catalogMeta, setCatalogMeta] = useState({});
  const [hosts, setHosts] = useState([]);
  const [overview, setOverview] = useState(null);

  const [selected, setSelected] = useState([]);
  const [allAgents, setAllAgents] = useState(!perHost);
  const [hostQuery, setHostQuery] = useState('');

  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogCat, setCatalogCat] = useState('');
  const [catalogSel, setCatalogSel] = useState([]);
  const [showCatalog, setShowCatalog] = useState(false);

  const [depStatus, setDepStatus] = useState('');
  const [depPackage, setDepPackage] = useState('');
  const [depQuery, setDepQuery] = useState('');

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [form, setForm] = useState({ name: '', package_id: '', vendor: '', default_version: '' });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const depFilters = perHost ? { ci_type: ciType, ci_id: ciId, limit: 200 } : { limit: 1000 };
      const [pkgRes, depRes, catRes] = await Promise.all([
        api.getSoftwarePackages(companyId),
        api.getSoftwareDeployments(companyId, depFilters),
        api.getSoftwareCatalog(companyId).catch(() => ({ catalog: [] })),
      ]);
      setPackages(pkgRes.packages || []);
      setMeta(pkgRes);
      setDeployments(depRes.deployments || []);
      setCatalog(catRes.catalog || []);
      setCatalogMeta(catRes);
      if (!perHost) {
        const [hostRes, ovRes] = await Promise.all([
          api.getPackageHosts(companyId).catch(() => ({ hosts: [] })),
          api.getSoftwareOverview(companyId).catch(() => null),
        ]);
        setHosts(hostRes.hosts || []);
        setOverview(ovRes);
      }
    } catch (err) {
      setError(err.message || 'No se pudo cargar la consola de paquetes');
    } finally {
      setLoading(false);
    }
  }, [companyId, ciType, ciId, perHost]);

  useEffect(() => { load(); }, [load]);

  // ── Destinos ────────────────────────────────────────────────────────────

  const visibleHosts = useMemo(() => {
    const q = hostQuery.trim().toLowerCase();
    if (!q) return hosts;
    return hosts.filter((h) => (
      `${h.name || ''} ${h.code || ''} ${h.kind || ''} ${h.agent_status || ''}`
        .toLowerCase()
        .includes(q)
    ));
  }, [hosts, hostQuery]);

  const outdatedHosts = useMemo(
    () => hosts.filter((h) => (
      h.agent_version && meta.min_agent_version
      && String(h.agent_version).localeCompare(String(meta.min_agent_version), undefined, { numeric: true }) < 0
    )),
    [hosts, meta.min_agent_version]
  );

  const toggleHost = (key) => {
    setSelected((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const selectVisibleHosts = () => {
    const keys = visibleHosts.map((h) => `${h.ci_type}:${h.ci_id}`);
    setSelected((prev) => Array.from(new Set([...prev, ...keys])));
  };

  const targetPayload = () => (
    perHost
      ? { targets: [{ ci_type: ciType, ci_id: ciId }] }
      : {
          all_agents: allAgents,
          targets: allAgents ? [] : selected.map((key) => {
            const [t, id] = key.split(':');
            return { ci_type: t, ci_id: Number(id) };
          }),
        }
  );

  const announce = (res) => {
    const n = res.count || 0;
    const skipped = res.skipped?.length || 0;
    setNotice(`Despliegue encolado en ${n} equipo(s)${skipped ? `; ${skipped} omitido(s)` : ''}.`);
  };

  const ready = perHost || allAgents || selected.length > 0;

  // ── Acciones ────────────────────────────────────────────────────────────

  const run = async (fn, okMessage) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fn();
      if (okMessage) setNotice(okMessage(res));
      await load();
    } catch (err) {
      setError(err.message || 'La operación ha fallado');
    } finally {
      setBusy(false);
    }
  };

  const deploy = (pkg) => {
    if (!confirmarAmbito(pkg.package_id)) return undefined;
    return run(
      () => api.deploySoftwarePackage({ package_id: pkg.id, ...targetPayload() }, companyId),
      announce
    );
  };

  const deployCatalogItem = (item) => {
    if (!confirmarAmbito(item.package_id)) return undefined;
    return run(
      () => api.deploySoftwarePackage({ choco_id: item.package_id, ...targetPayload() }, companyId),
      announce
    );
  };

  const retryDeployment = (d) => run(() => api.retrySoftwareDeployment(d.id, companyId));
  const cancelDeployment = (d) => run(() => api.cancelSoftwareDeployment(d.id, companyId));
  const retryAllErrors = () => run(
    () => api.retryAllSoftwareErrors({}, companyId),
    (res) => `${res.retried} despliegue(s) en error reintentados.`
  );

  const upgradeAll = () => run(
    () => api.upgradeAllSoftware(targetPayload(), companyId),
    (res) => `Actualización de paquetes encolada en ${res.count || 0} equipo(s).`
  );

  const importCatalog = (scope) => run(
    () => api.importSoftwareCatalog(scope ? { scope } : { package_ids: catalogSel }, companyId),
    (res) => {
      setCatalogSel([]);
      return `${res.created} paquete(s) añadidos${res.already ? `; ${res.already} ya estaban` : ''}.`;
    }
  );

  const createPackage = async (e) => {
    e.preventDefault();
    if (!form.package_id.trim()) {
      setError('Indica el id de Chocolatey (por ejemplo 7zip)');
      return;
    }
    await run(
      () => api.createSoftwarePackage({
        name: form.name || form.package_id,
        package_id: form.package_id,
        vendor: form.vendor,
        default_version: form.default_version,
      }, companyId),
      () => {
        setForm({ name: '', package_id: '', vendor: '', default_version: '' });
        setShowForm(false);
        return 'Paquete añadido al catálogo.';
      }
    );
  };

  const removePackage = async (pkg) => {
    if (!window.confirm(`¿Quitar ${pkg.name} del catálogo? No desinstala nada en los equipos.`)) return;
    await run(() => api.deleteSoftwarePackage(pkg.id, companyId));
  };

  const checkChoco = () => run(
    () => api.getChocoStatus(ciType, ciId, companyId),
    () => 'Diagnóstico de Chocolatey encolado. El resultado sale en la pestaña Agente del equipo.'
  );

  // ── Datos derivados ─────────────────────────────────────────────────────

  const visibleCatalog = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    return catalog.filter((item) => {
      if (catalogCat && item.category !== catalogCat) return false;
      if (!q) return true;
      return `${item.name} ${item.package_id} ${item.vendor || ''} ${item.description || ''}`
        .toLowerCase()
        .includes(q);
    });
  }, [catalog, catalogQuery, catalogCat]);

  const visibleDeployments = useMemo(() => {
    const q = depQuery.trim().toLowerCase();
    return deployments.filter((d) => {
      if (depStatus && d.status !== depStatus) return false;
      if (depPackage && String(d.package_id) !== String(depPackage)) return false;
      if (!q) return true;
      return `${d.host_name || ''} ${d.package?.name || ''} ${d.package?.package_id || ''}`
        .toLowerCase()
        .includes(q);
    });
  }, [deployments, depStatus, depPackage, depQuery]);

  const localStats = useMemo(() => ({
    total: deployments.length,
    done: deployments.filter((d) => d.status === 'done').length,
    error: deployments.filter((d) => d.status === 'error').length,
    running: deployments.filter((d) => ['pending', 'queued', 'running'].includes(d.status)).length,
  }), [deployments]);

  const exportDeployments = async () => {
    const rows = visibleDeployments.map((d) => ({
      Fecha: fmtDate(d.created_at),
      Equipo: d.host_name || `${d.ci_type}:${d.ci_id}`,
      Paquete: d.package?.name || d.package?.package_id || '',
      'Id Chocolatey': d.package?.package_id || '',
      Estado: STATUS_LABEL[d.status] || d.status,
      Versión: d.installed_version || d.requested_version || '',
      Error: d.error || '',
    }));
    await exportRowsToExcel(rows, {
      fileBase: 'despliegues-chocolatey',
      sheetName: 'Despliegues',
    });
  };

  const byPackage = overview?.by_package || [];

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="software-packages">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h3 className="dv-section-title" style={{ margin: 0 }}>
            Software con Chocolatey{perHost ? ` · ${hostName || ''}` : ' · consola central'}
            <HelpDot topic="agente.paquetes" label="Ayuda: paquetes de Chocolatey" />
          </h3>
          <div className="sf2-hint">
            {perHost
              ? 'Instala paquetes en este equipo. Requiere agente 2.6.11 o superior.'
              : 'Lanza instalaciones masivas y controla todo lo desplegado en el parque.'}
          </div>
        </div>
        <button type="button" className="wol-btn-secondary" onClick={() => setShowForm((v) => !v)} disabled={busy}>
          {showForm ? 'Cancelar' : 'Añadir paquete'}
        </button>
        <button
          type="button"
          className="wol-btn-secondary"
          onClick={upgradeAll}
          disabled={busy || !ready}
          title="choco upgrade all: actualiza navegadores, utilidades y drivers empaquetados (lo que no viene de Windows Update)"
        >
          Actualizar todo (Choco)
        </button>
        {perHost && (
          <button type="button" className="wol-btn-secondary" onClick={checkChoco} disabled={busy}>
            Comprobar Chocolatey
          </button>
        )}
        <button type="button" className="wol-btn-secondary" onClick={load} disabled={loading || busy}>
          {loading ? 'Cargando…' : 'Recargar'}
        </button>
      </div>

      {error && <div className="alert alert-danger" style={{ padding: '8px 12px', marginBottom: 10 }}>{error}</div>}
      {notice && <div className="alert alert-success" style={{ padding: '8px 12px', marginBottom: 10 }}>{notice}</div>}

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {perHost ? (
          <>
            <KPI label="Despliegues" value={localStats.total} />
            <KPI label="Instalados" value={localStats.done} tone="var(--green-600, #16a34a)" />
            <KPI label="En curso" value={localStats.running} tone="var(--accent-600, #2563eb)" />
            <KPI label="Con error" value={localStats.error} tone="var(--red-600, #dc2626)" />
          </>
        ) : (
          <>
            <KPI label="Paquetes en catálogo" value={overview?.packages?.total ?? packages.length} />
            <KPI label="Equipos con agente" value={hosts.length} />
            <KPI label="Instalaciones OK" value={overview?.deployments?.done ?? 0} tone="var(--green-600, #16a34a)" />
            <KPI label="En curso" value={overview?.deployments?.running ?? 0} tone="var(--accent-600, #2563eb)" />
            <KPI label="Con error" value={overview?.deployments?.error ?? 0} tone="var(--red-600, #dc2626)" />
          </>
        )}
      </div>

      {/* Salud de los despliegues: mismo criterio visual que parches y seguridad. */}
      {!perHost && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(300px, 1fr)', gap: 10, marginBottom: 12 }}>
          <StatDoughnut
            title="Salud de los despliegues"
            centerLabel="TOTAL"
            segments={[
              { label: 'Instalados', value: overview?.deployments?.done ?? 0, color: '#16a34a' },
              { label: 'En curso', value: overview?.deployments?.running ?? 0, color: '#2563eb' },
              { label: 'Con error', value: overview?.deployments?.error ?? 0, color: '#dc2626' },
              { label: 'Cancelados', value: overview?.deployments?.cancelled ?? 0, color: '#94a3b8' },
            ]}
          />
          <StatDoughnut
            title="Cobertura del parque"
            centerLabel="EQUIPOS"
            segments={[
              { label: 'Con software instalado', value: overview?.hosts_with_installs ?? 0, color: '#16a34a' },
              { label: 'Sin nada desplegado', value: Math.max(0, hosts.length - (overview?.hosts_with_installs ?? 0)), color: '#eab308' },
            ]}
          />
        </div>
      )}

      {/* Aplicaciones más instaladas: el dato que se opera de verdad. */}
      {!perHost && (
        <TopInstalledApps
          byPackage={overview?.by_package}
          hostsCount={hosts.length}
          onDeploy={(p) => {
            const pkg = packages.find((x) => String(x.id) === String(p.package_id));
            if (pkg) deploy(pkg);
          }}
        />
      )}

      {showForm && (
        <form onSubmit={createPackage} className="sf2-card" style={{ marginBottom: 12 }}>
          <h3>Nuevo paquete</h3>
          <div className="sf2-fields" style={{ '--n': 2 }}>
            <div className="sf2-field">
              <label>Id de Chocolatey *</label>
              <input
                value={form.package_id}
                onChange={(e) => setForm({ ...form, package_id: e.target.value })}
                placeholder="7zip"
              />
            </div>
            <div className="sf2-field">
              <label>Nombre visible</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="7-Zip"
              />
            </div>
            <div className="sf2-field">
              <label>Fabricante</label>
              <input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Igor Pavlov" />
            </div>
            <div className="sf2-field">
              <label>Versión por defecto</label>
              <input
                value={form.default_version}
                onChange={(e) => setForm({ ...form, default_version: e.target.value })}
                placeholder="(última)"
              />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <button type="submit" className="dv-btn dv-btn-primary" disabled={busy}>Guardar paquete</button>
          </div>
        </form>
      )}

      {/* Destinos (solo consola) */}
      {!perHost && (
        <div className="sf2-card" style={{ marginBottom: 12 }}>          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, flex: 1 }}>Destinos</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={allAgents} onChange={(e) => setAllAgents(e.target.checked)} />
              Todos los equipos con agente ({hosts.length})
            </label>
          </div>

          <div className="sf2-hint" style={{ marginTop: 4 }}>
            Con los destinos seleccionados: <strong>Desplegar</strong> instala un paquete concreto y{' '}
            <strong>Actualizar todo (Choco)</strong> ejecuta `choco upgrade all` (software que no viene de Windows Update).
          </div>

          {/* Resumen del alcance: qué equipos van a recibir lo que se lance. */}
          <div
            style={{
              marginTop: 8,
              padding: '8px 10px',
              borderRadius: 8,
              background: 'var(--bg-subtle, rgba(148,163,184,0.10))',
              border: '1px solid var(--border)',
              fontSize: 13,
            }}
          >
            <strong>Se instalará en:</strong>{' '}
            {allAgents
              ? `los ${hosts.length} equipos con agente`
              : (selected.length === 0
                ? 'ningún equipo todavía — marca alguno abajo'
                : `${selected.length} equipo(s): ${
                  hosts
                    .filter((h) => selected.includes(h.ci_id))
                    .map((h) => h.name)
                    .join(', ')
                }`)}
          </div>

          {!allAgents && (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0' }}>
                <input
                  value={hostQuery}
                  onChange={(e) => setHostQuery(e.target.value)}
                  placeholder="Buscar equipo…"
                  style={{ flex: 1, minWidth: 180 }}
                />
                <button type="button" className="wol-btn-secondary" onClick={selectVisibleHosts} disabled={busy}>
                  Seleccionar visibles ({visibleHosts.length})
                </button>
                <button type="button" className="wol-btn-secondary" onClick={() => setSelected([])} disabled={busy}>
                  Quitar selección
                </button>
              </div>
              <div style={{
                maxHeight: 220,
                overflow: 'auto',
                border: '1px solid var(--border-color, #e5e7eb)',
                borderRadius: 8,
                padding: 8,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 4,
              }}>
                {visibleHosts.map((h) => {
                  const key = `${h.ci_type}:${h.ci_id}`;
                  const outdated = h.agent_version && meta.min_agent_version
                    && String(h.agent_version).localeCompare(String(meta.min_agent_version), undefined, { numeric: true }) < 0;
                  return (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                      <input
                        type="checkbox"
                        checked={selected.includes(key)}
                        onChange={() => toggleHost(key)}
                      />
                      <span style={{ flex: 1 }}>{h.name}</span>
                      <span className="sf2-hint">{h.kind}</span>
                      {outdated && <span className="sf2-pill sf2-pill--wa" title={`Agente ${h.agent_version}`}>antiguo</span>}
                    </label>
                  );
                })}
                {visibleHosts.length === 0 && (
                  <div className="sf2-hint">Ningún equipo coincide con la búsqueda.</div>
                )}
              </div>
              <div className="sf2-hint" style={{ marginTop: 6 }}>
                {selected.length} equipo(s) seleccionados
                {outdatedHosts.length > 0 && ` · ${outdatedHosts.length} con agente anterior a ${meta.min_agent_version}`}
              </div>
            </>
          )}
        </div>
      )}

      {/* Catálogo predefinido */}
      <div className="sf2-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h3 style={{ margin: 0 }}>
              Catálogo predefinido{' '}
              <span className="sf2-hint" style={{ fontWeight: 400 }}>
                {catalog.length} paquetes · {catalogMeta.recommended_count || 0} recomendados
              </span>
            </h3>
            <div className="sf2-hint">
              Selecciona y despliega sin teclear ids. Si el paquete no está en el catálogo, se añade solo.
            </div>
          </div>
          <button type="button" className="wol-btn-secondary" onClick={() => setShowCatalog((v) => !v)} disabled={busy}>
            {showCatalog ? 'Ocultar catálogo' : 'Ver catálogo'}
          </button>
          {!perHost && (
            <button type="button" className="wol-btn-secondary" onClick={() => importCatalog('recommended')} disabled={busy}>
              Añadir recomendados
            </button>
          )}
        </div>

        {showCatalog && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0' }}>
              <input
                value={catalogQuery}
                onChange={(e) => setCatalogQuery(e.target.value)}
                placeholder="Buscar: chrome, pdf, 7zip, vlc…"
                style={{ flex: 1, minWidth: 200 }}
              />
              <select value={catalogCat} onChange={(e) => setCatalogCat(e.target.value)} style={{ minWidth: 180 }}>
                <option value="">Todas las categorías</option>
                {Object.entries(catalogMeta.categories || {}).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="dv-table-wrap" style={{ maxHeight: 380, overflow: 'auto' }}>
              <table className="dv-table">
                <thead>
                  <tr>
                    <th style={{ width: 34 }} />
                    <th>Paquete</th>
                    <th>Categoría</th>
                    <th>Descripción</th>
                    <th style={{ width: 150 }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCatalog.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 14 }}>Sin paquetes con esa búsqueda.</td></tr>
                  )}
                  {visibleCatalog.map((item) => (
                    <tr key={item.package_id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={catalogSel.includes(item.package_id)}
                          disabled={item.added}
                          onChange={(e) => setCatalogSel((prev) => (
                            e.target.checked
                              ? [...prev, item.package_id]
                              : prev.filter((id) => id !== item.package_id)
                          ))}
                        />
                      </td>
                      <td>
                        <strong>{item.name}</strong>
                        <div className="sf2-hint">
                          <code>{item.package_id}</code>{item.recommended ? ' · recomendado' : ''}
                        </div>
                      </td>
                      <td>{(catalogMeta.categories || {})[item.category] || item.category}</td>
                      <td><span className="sf2-hint">{item.description}</span></td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {item.added && <span className="sf2-pill sf2-pill--ok">En catálogo</span>}
                          <button
                            type="button"
                            className="wol-btn-secondary"
                            disabled={busy || (!perHost && !ready)}
                            onClick={() => deployCatalogItem(item)}
                          >
                            {perHost ? 'Instalar' : 'Desplegar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!perHost && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button type="button" className="wol-btn-secondary" disabled={busy || !catalogSel.length} onClick={() => importCatalog()}>
                  Añadir seleccionados ({catalogSel.length})
                </button>
                <button type="button" className="wol-btn-secondary" disabled={busy} onClick={() => importCatalog('all')}>
                  Añadir todo el catálogo
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Instalado en el parque */}
      {!perHost && byPackage.length > 0 && (
        <div className="sf2-card" style={{ marginBottom: 12 }}>
          <h3 style={{ marginTop: 0 }}>Instalado en el parque</h3>
          <div className="dv-table-wrap">
            <table className="dv-table">
              <thead>
                <tr>
                  <th>Paquete</th>
                  <th style={{ width: 90 }}>OK</th>
                  <th style={{ width: 90 }}>Errores</th>
                  <th style={{ width: 90 }}>En curso</th>
                  <th style={{ width: 160 }}>Último despliegue</th>
                  <th style={{ width: 150 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {byPackage.map((row) => (
                  <tr key={row.package_id}>
                    <td>
                      <strong>{row.name}</strong>
                      <div className="sf2-hint">{row.total} despliegue(s)</div>
                    </td>
                    <td><span className="sf2-pill sf2-pill--ok">{row.done}</span></td>
                    <td>
                      {row.error
                        ? <span className="sf2-pill sf2-pill--bad">{row.error}</span>
                        : <span className="sf2-hint">0</span>}
                    </td>
                    <td>{row.running || 0}</td>
                    <td>{fmtDate(row.last_at)}</td>
                    <td>
                      {row.error > 0 && (
                        <button
                          type="button"
                          className="wol-btn-secondary"
                          disabled={busy}
                          onClick={() => run(
                            () => api.retryAllSoftwareErrors({ package_id: row.package_id }, companyId),
                            (res) => `${res.retried} reintento(s) en ${row.name}.`
                          )}
                        >
                          Reintentar errores
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Catálogo de la empresa */}
      <div className="dv-table-wrap">
        <table className="dv-table">
          <thead>
            <tr>
              <th>Paquete</th>
              <th>Id Chocolatey</th>
              <th>Versión</th>
              <th>Estado</th>
              <th style={{ width: 190 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {packages.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 16 }}>Sin paquetes en el catálogo. Añade uno o usa el catálogo predefinido.</td></tr>
            )}
            {packages.map((pkg) => {
              const stats = pkg.stats || {};
              return (
                <tr key={pkg.id}>
                  <td>
                    <strong>{pkg.name}</strong>
                    {pkg.vendor && <div className="sf2-hint">{pkg.vendor}</div>}
                  </td>
                  <td><code>{pkg.package_id}</code></td>
                  <td>{pkg.default_version || 'última'}</td>
                  <td>
                    {stats.total ? (
                      <span className="sf2-hint">
                        {stats.done || 0} ok · {stats.error || 0} con error · {stats.running || 0} en curso
                      </span>
                    ) : <span className="sf2-hint">sin despliegues</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="wol-btn-secondary"
                        disabled={busy || (!perHost && !ready)}
                        onClick={() => deploy(pkg)}
                        title={!perHost && !ready ? 'Selecciona algún equipo destino' : undefined}
                      >
                        {perHost ? 'Instalar' : 'Desplegar'}
                      </button>
                      {!compact && (
                        <button type="button" className="wol-btn-secondary" disabled={busy} onClick={() => removePackage(pkg)}>
                          Quitar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Despliegues */}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <h3 className="dv-section-title" style={{ margin: 0, flex: 1 }}>
            Despliegues
            {localStats.running > 0 && <span className="sf2-pill sf2-pill--info">{localStats.running} en curso</span>}
          </h3>
          {!perHost && localStats.error > 0 && (
            <button type="button" className="wol-btn-secondary" disabled={busy} onClick={retryAllErrors}>
              Reintentar errores ({localStats.error})
            </button>
          )}
          <button type="button" className="wol-btn-secondary" disabled={!visibleDeployments.length} onClick={exportDeployments}>
            Exportar Excel
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <input
            value={depQuery}
            onChange={(e) => setDepQuery(e.target.value)}
            placeholder="Buscar por equipo o paquete…"
            style={{ flex: 1, minWidth: 180 }}
          />
          <select value={depStatus} onChange={(e) => setDepStatus(e.target.value)} style={{ minWidth: 150 }}>
            <option value="">Todos los estados</option>
            <option value="done">Instalados</option>
            <option value="error">Con error</option>
            <option value="running">Instalando</option>
            <option value="pending">Pendientes</option>
            <option value="cancelled">Cancelados</option>
          </select>
          <select value={depPackage} onChange={(e) => setDepPackage(e.target.value)} style={{ minWidth: 180 }}>
            <option value="">Todos los paquetes</option>
            {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        <div className="dv-table-wrap">
          <table className="dv-table">
            <thead>
              <tr>
                <th>Fecha</th>
                {!perHost && <th>Equipo</th>}
                <th>Paquete</th>
                <th>Estado</th>
                <th>Versión</th>
                <th>Detalle</th>
                <th style={{ width: 150 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibleDeployments.length === 0 && (
                <tr><td colSpan={perHost ? 6 : 7} style={{ textAlign: 'center', padding: 16 }}>Sin despliegues con esos filtros.</td></tr>
              )}
              {visibleDeployments.map((d) => (
                <tr key={d.id}>
                  <td>{fmtDate(d.created_at)}</td>
                  {!perHost && <td>{d.host_name || `${d.ci_type}:${d.ci_id}`}</td>}
                  <td>{d.package?.name || d.package?.package_id || '—'}</td>
                  <td><span className={STATUS_PILL[d.status] || 'sf2-pill'}>{STATUS_LABEL[d.status] || d.status_label || d.status}</span></td>
                  <td>{d.installed_version || d.requested_version || '—'}</td>
                  <td style={{ maxWidth: 320 }}>
                    {d.error
                      ? <span className="sf2-hint" title={d.error}>{d.error.slice(0, 120)}</span>
                      : <span className="sf2-hint">—</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {d.status === 'error' && (
                        <button type="button" className="wol-btn-secondary" disabled={busy} onClick={() => retryDeployment(d)}>
                          Reintentar
                        </button>
                      )}
                      {['pending', 'queued'].includes(d.status) && (
                        <button type="button" className="wol-btn-secondary" disabled={busy} onClick={() => cancelDeployment(d)}>
                          Cancelar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
